<template>
  <div class="payouts-registry-created">
    <loader
      class="payouts-registry-created__loader"
      v-if="isRegistryProcessing || isRegistryPositionDeleting"
    ></loader>
    <transactions-page-controls
      :has-stats="false"
      :has-export="false"
      :has-refresh="false"
      :has-filter="false"
      :stats="registryStats"
      :specific-actions-to-right="true"
    >
      <template #specificActions>
        <primary-button
          class="payouts-registry-created__action"
          :label="$t('payouts.by-registry.created.actions.process-registry')"
          @click="processPayoutsRegistry"
        ></primary-button>
      </template>
    </transactions-page-controls>
    <div
      class="payouts-registry-warning"
      v-if="hasWarning && showErrorsWarning"
    >
      <div class="payouts-registry-warning__icon">
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 7.5C0 3.35786 3.35786 0 7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15C3.35786 15 0 11.6421 0 7.5Z"
            fill="#FDE9E9"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.49982 9.54541C7.87638 9.54541 8.18164 9.78962 8.18164 10.0909L8.18164 10.3636C8.18164 10.6648 7.87638 10.909 7.49982 10.909C7.12326 10.909 6.818 10.6648 6.818 10.3636L6.818 10.0909C6.818 9.78962 7.12326 9.54541 7.49982 9.54541Z"
            fill="#DD6262"
          />
          <path
            d="M6.82407 3.62199C6.77146 3.14756 7.08904 2.72729 7.50018 2.72729C7.91131 2.72729 8.2289 3.14756 8.17628 3.62199L7.69242 7.98517C7.66334 8.2474 7.33702 8.2474 7.30794 7.98517L6.82407 3.62199Z"
            fill="#DD6262"
          />
        </svg>
      </div>
      <div class="payouts-registry-warning__content">
        <div class="payouts-registry-warning__close" @click="hideErrorsWarning">
          {{ $t('payouts.by-registry.created.warning.close') }}
        </div>
        <div class="payouts-registry-warning__title">
          {{ $t('payouts.by-registry.created.warning.title') }}
        </div>
        <div class="payouts-registry-warning__text">
          {{ $t('payouts.by-registry.created.warning.text') }}
        </div>
        <div class="payouts-registry-warning__rows">
          <div class="payouts-registry-warning__rows-title">
            {{
              $t('payouts.by-registry.created.warning.rows-title', {
                count: errorsRows.length,
              })
            }}
          </div>
          <div class="payouts-registry-warning__rows-list">
            <div
              class="payouts-registry-warning__rows-item"
              v-for="(errorRow, index) in errorsRows"
              :key="`error-row-${index}`"
            >
              {{ errorRow }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <payouts-registry-created-table
      class="payouts-registry-created__table"
      :payouts-registry-items="payoutsRegistryPositionsList || []"
      @registryPositionUpdated="updatePayoutsRegistryItem"
      @registryPositionDeleted="deletePayoutsRegistryItem"
    >
    </payouts-registry-created-table>
    <payouts-registry-item-delete-popup
      ref="payoutsRegistryItemDeleteConfirmPopup"
    ></payouts-registry-item-delete-popup>
    <success-notification
      ref="payoutsRegistryProcessingNotification"
      :text="
        $t('payouts.by-registry.created.notifications.registry-in-progress')
      "
    ></success-notification>
  </div>
</template>

<script>
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import TransactionsPageControls from '@vue-app/components/company/transactions/shared/transactions-page-controls/TransactionsPageControls.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import { Stats } from '@js-app/entities/transaction/Stats'
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import PayoutsRegistryCreatedTable from '@vue-app/components/company/payouts/by-registry/table/PayoutsRegistryCreatedTable.vue'
import { mapState } from 'vuex'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import PayoutsRegistryItemDeletePopup from '@vue-app/components/company/payouts/by-registry/popup/PayoutsRegistryItemDeletePopup.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import Loader from '@vue-app/components/base/Loader.vue'
import { formatStringNumberToCoin } from '@js-app/services/helpers/numbers'

export default {
  name: 'PayoutsRegistryCreated',
  components: {
    Loader,
    SuccessNotification,
    PayoutsRegistryItemDeletePopup,
    PayoutsRegistryCreatedTable,
    TransactionsTable,
    RequireAccess,
    TransactionsPageControls,
    PrimaryButton,
  },
  props: {
    payoutsRegistry: {
      type: Object,
    },
    payoutsRegistryPositions: {
      type: Object,
    },
  },
  data() {
    return {
      payoutsRegistryPositionsList: [],
      isRegistryPositionDeleting: false,
      isRegistryProcessing: false,
      refreshKey: 0,
      showErrorsWarning: true,
    }
  },

  computed: {
    ...mapState(['selectedCompany']),
    registryStats() {
      let totalSum = 0

      this.payoutsRegistryPositions.list.forEach((_position) => {
        totalSum += _position.sum
      })

      return new Stats({
        OperationsStats: {
          All: {
            Sum: totalSum,
            Count: this.payoutsRegistryPositions.registry_stat.position_count,
          },
        },
      })
    },
    hasWarning() {
      return (
        this.payoutsRegistryPositions.errors.findIndex(
          (_val) => Object.keys(_val.error_string).length > 0
        ) > -1
      )
    },
    errorsRows() {
      let _errorsRows = []

      if (!this.hasWarning) {
        return _errorsRows
      }

      this.payoutsRegistryPositions.errors.map((_error) => {
        if (Object.keys(_error.error_string).length > 0) {
          let _payoutsRegistryItem = this.payoutsRegistryPositionsList.find(
            (_position) => _position.id === _error.position_id
          )

          if (!_payoutsRegistryItem) {
            return
          }

          _errorsRows.push(
            `${_payoutsRegistryItem.pan};${_payoutsRegistryItem.fio};${_payoutsRegistryItem.sum}`
          )
        }
      })

      return _errorsRows
    },
  },
  created() {
    this.payoutsRegistryPositionsList =
      this.payoutsRegistryPositions?.list || []
  },
  methods: {
    async updatePayoutsRegistryItem(registryPositionItem) {
      this.payoutsRegistryPositionsList.find(
        (_item) => _item.id === registryPositionItem.id
      ).sum = formatStringNumberToCoin(
        parseFloat(
          registryPositionItem.sum.replace(/\s+/, '').replace(',', '.')
        )
      )

      this.refreshKey++

      await CompaniesApi.updateRegistryPosition(
        this.selectedCompany,
        registryPositionItem.registry_id,
        {
          id: registryPositionItem.id,
          sum: parseFloat(
            registryPositionItem.sum.replace(/\s+/, '').replace(',', '.')
          ),
          fio: registryPositionItem.fio,
          ipn: registryPositionItem.ipn ?? '',
          pan: registryPositionItem.pan,
        }
      )
    },
    async deletePayoutsRegistryItem(registryPositionItem) {
      let isConfirm =
        await this.$refs.payoutsRegistryItemDeleteConfirmPopup.open()

      if (!isConfirm) {
        return
      }

      this.isRegistryPositionDeliting = true

      try {
        await CompaniesApi.deletePayoutsRegistryPosition(
          this.selectedCompany,
          registryPositionItem.registry_id,
          registryPositionItem.id
        )
        this.payoutsRegistryPositionsList.splice(
          this.payoutsRegistryPositionsList.findIndex(
            (_position) =>
              parseInt(_position.id) === parseInt(registryPositionItem.id)
          ),
          1
        )
      } finally {
        this.isRegistryPositionDeleting = false
      }
    },
    async processPayoutsRegistry() {
      this.isRegistryProcessing = true
      try {
        this.$refs.payoutsRegistryProcessingNotification.show()

        await CompaniesApi.processPayoutsRegistry(
          this.selectedCompany,
          this.payoutsRegistry.id
        )
        window.location.reload()
      } finally {
        this.isRegistryProcessing = false
      }
    },
    hideErrorsWarning() {
      this.showErrorsWarning = false
    },
  },
}
</script>

<style lang="scss" scoped>
.payouts-registry-created {
  position: relative;
  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 99;
  }

  &__table {
    margin-top: 21px;
  }
}
.payouts-registry-warning {
  padding: 12px 20px 30px;
  background: #fff2f2;
  border-radius: 18px;
  margin: 15px 0 10px;
  display: grid;
  grid-template-columns: 30px 1fr;
  column-gap: 24px;
  &__content {
    position: relative;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: #fffafa;
    border-radius: 24px;
  }

  &__title {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: #dd6262;
    margin-bottom: 3px;
  }

  &__text {
    font-weight: 500;
    font-size: 11px;
    line-height: 15px;
    color: #585f6d;
  }

  &__close {
    position: absolute;
    top: 0;
    right: 20px;
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #64748B;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  &__rows {
    margin-top: 10px;

    &-title,
    &-item {
      font-weight: 400;
      font-size: 11px;
      line-height: 15px;
      color: #585f6d;
    }
  }
}
</style>
